/**
 * The card reader beside this till, and taking a card on it.
 *
 * Any card machine works (`payments.terminal`): the till shows the amount,
 * somebody keys it on the machine, and presses what the machine showed. The
 * money is recorded only on "Approved", and only once however often it is
 * pressed — the server keys the payment on the attempt.
 *
 * Which reader is chosen per tablet and kept on the device, like the kitchen
 * screen's station: the machine next to a till does not change with whoever
 * signs in. No reader chosen, and a card is taken as before, typed in by hand.
 */

import { useState } from 'react';
import {
  formatMoney,
  useApproveCard,
  useCloseCard,
  type Bill,
  type CardAttempt,
} from '@restaurant/api-client';
import { ActionError, Button, Field, Input, Segmented, Sheet } from '@restaurant/ui';

import { useTillReader } from './till-reader';

/** "Card reader" in the tablet's sheet. Only there once a site has readers. */
export function ReaderPicker({ locationId }: { locationId: string }) {
  const { entitled, readers, reader, choose } = useTillReader(locationId);
  if (!entitled || readers.length === 0) return null;

  return (
    <div className="pos__till">
      <span className="pos__till-label">Card reader</span>
      <Segmented
        label="Card reader"
        value={reader?.id ?? 'none'}
        onChange={(next) => choose(next === 'none' ? null : next)}
        scroll
        options={[
          { value: 'none', label: 'Typed in' },
          ...readers.map((r) => ({ value: r.id, label: r.name })),
        ]}
      />
    </div>
  );
}

/**
 * A card waiting on the reader: the amount to key in, and what it showed.
 *
 * "Approved" is the big button because it is the common answer, and it is
 * the only one that records money. The slip's code and last four digits are
 * optional and folded away: a queue at the till matters more than a field
 * nobody reads, and the reader keeps its own record.
 */
export function CardWaiting({
  attempt,
  onPaid,
  onClosed,
}: {
  attempt: CardAttempt;
  onPaid: (bill: Bill) => void;
  onClosed: (declined: boolean) => void;
}) {
  const approve = useApproveCard();
  const close = useCloseCard();
  const [details, setDetails] = useState(false);
  const [code, setCode] = useState('');
  const [last4, setLast4] = useState('');
  const total = (Number(attempt.amount) + Number(attempt.tip)).toFixed(2);

  return (
    <Sheet
      title={`Card on ${attempt.reader_name}`}
      onDismiss={() =>
        close.mutate(
          { attemptId: attempt.id, declined: false },
          { onSettled: () => onClosed(false) },
        )
      }
      actions={
        <div className="cw__actions">
          <Button
            variant="primary"
            size="lg"
            block
            loading={approve.isPending}
            onClick={() =>
              approve.mutate(
                {
                  attemptId: attempt.id,
                  ...(code.trim() ? { auth_code: code.trim() } : {}),
                  ...(last4.trim() ? { card_last4: last4.trim() } : {}),
                },
                { onSuccess: onPaid },
              )
            }
          >
            Approved
          </Button>
          <Button
            size="lg"
            block
            className="cw__declined"
            loading={close.isPending && close.variables?.declined === true}
            onClick={() =>
              close.mutate(
                { attemptId: attempt.id, declined: true },
                { onSuccess: () => onClosed(true) },
              )
            }
          >
            Declined
          </Button>
        </div>
      }
    >
      <p className="cw__amount tabular">{formatMoney(total)}</p>
      <p className="cw__prompt">
        Key this on <strong>{attempt.reader_name}</strong>, then press what it shows.
        {Number(attempt.tip) > 0 &&
          ` That is ${formatMoney(attempt.amount)} and a ${formatMoney(attempt.tip)} tip.`}
      </p>

      {details ? (
        <div className="cw__details">
          <Field label="Code on the slip" hint="Optional">
            <Input value={code} maxLength={40} onChange={(e) => setCode(e.target.value)} />
          </Field>
          <Field label="Card's last four digits" hint="Optional">
            <Input
              value={last4}
              inputMode="numeric"
              maxLength={4}
              onChange={(e) => setLast4(e.target.value.replace(/\D/g, ''))}
            />
          </Field>
        </div>
      ) : (
        <button type="button" className="cw__more" onClick={() => setDetails(true)}>
          Add the slip&rsquo;s code
        </button>
      )}

      <ActionError error={approve.error ?? close.error} action="The card payment" />
    </Sheet>
  );
}
